import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Landmark, RefreshCw, Download, AlertTriangle, CheckCircle2, FileText, Wallet, ShieldOff,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { EmptyState, CardSkeleton } from './common/States';
import { api } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** PF is filed for a completed month, so the default is the previous one. */
const previousMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return { month: d.getMonth() + 1, year: d.getFullYear() };
};

const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PeriodPicker = ({ period, onChange }) => (
  <div className="flex items-end gap-2">
    <div>
      <Label className="text-xs text-muted-foreground">Month</Label>
      <select
        value={period.month}
        onChange={(e) => onChange({ ...period, month: Number(e.target.value) })}
        className="mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm"
      >
        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
    </div>
    <div>
      <Label className="text-xs text-muted-foreground">Year</Label>
      <Input
        type="number"
        className="mt-1 w-28"
        value={period.year}
        onChange={(e) => onChange({ ...period, year: Number(e.target.value) })}
      />
    </div>
  </div>
);

/* ─────────────────────────────── policy ─────────────────────────────── */

const PfPolicy = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/pf/policy');
      setForm(data.policy || {
        establishment_code: '', establishment_name: '',
        ...(data.defaults || {}), contribute_on_full_wages: false,
      });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load the PF policy'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/pf/policy', form);
      toast.success('PF policy saved');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save the policy'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <CardSkeleton rows={6} />;

  const field = (key, label, hint) => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        className="mt-1"
        value={form[key] ?? ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Landmark className="h-4 w-4" />Establishment &amp; rates
        </CardTitle>
        <p className="text-[13px] text-muted-foreground">
          Contributions are computed on the wage ceiling unless the establishment
          contributes on full wages. The pension share stays capped at the ceiling
          either way — that limit is statutory, not a choice.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          {field('establishment_code', 'Establishment code')}
          {field('establishment_name', 'Establishment name')}
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {field('wage_ceiling', 'Wage ceiling')}
          {field('employee_rate', 'Employee rate %')}
          {field('employer_rate', 'Employer rate %')}
          {field('pension_rate', 'Pension rate %', 'Of the employer share')}
          {field('admin_charge_rate', 'Admin charges %')}
          {field('edli_rate', 'EDLI %')}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch
            checked={Boolean(form.contribute_on_full_wages)}
            onCheckedChange={(v) => setForm({ ...form, contribute_on_full_wages: v })}
          />
          Contribute on full wages rather than the ceiling
        </label>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save policy'}</Button>
      </CardContent>
    </Card>
  );
};

/* ────────────────────────────── UAN profiles ────────────────────────────── */

/**
 * Exempting a member removes them from every contribution and every return, so
 * it asks for a reason and says what it will do. Restoring is the same decision
 * in reverse and needs no reason — putting somebody back into a return is not
 * the risky direction.
 */
const ExemptionDialog = ({ profile, onClose, onSaved }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const exempting = profile ? !profile.is_exempt : false;

  useEffect(() => { setReason(''); }, [profile?.employee_id]);

  if (!profile) return null;

  const submit = async () => {
    if (exempting && !reason.trim()) {
      toast.error('A reason is needed — an exemption without one cannot be explained later');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/pf/profiles/${profile.employee_id}`, {
        is_exempt: exempting,
        exemption_reason: exempting ? reason.trim() : '',
      });
      toast.success(exempting ? 'Member exempted from PF' : 'Member restored to PF');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not change the exemption'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {exempting ? `Exempt ${profile.employee_id} from PF?` : `Restore ${profile.employee_id} to PF?`}
          </DialogTitle>
          <DialogDescription>
            {exempting
              ? 'They will be left out of every monthly contribution and every ECR from now on. Months already generated are not changed.'
              : 'They will be included in contributions and returns generated from now on.'}
          </DialogDescription>
        </DialogHeader>

        {exempting ? (
          <div className="space-y-3">
            <div className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[13px] text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                An exemption is a statutory position, not a convenience. If this is
                only to fix a wrong figure for one month, correct the contribution
                instead.
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">
                Reason for exemption (recorded and auditable)
              </Label>
              <Textarea
                className="mt-1"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. International worker covered by a social security agreement"
              />
            </div>
          </div>
        ) : profile.exemption_reason ? (
          <p className="text-[13px] text-muted-foreground">
            Currently exempt: <span className="text-foreground">{profile.exemption_reason}</span>
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || (exempting && !reason.trim())}
            variant={exempting ? 'destructive' : 'default'}
          >
            {saving ? 'Saving…' : exempting ? 'Exempt from PF' : 'Restore to PF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PfProfiles = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ employee_id: '', uan: '' });
  const [saving, setSaving] = useState(false);
  const [exemptionTarget, setExemptionTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/api/pf/profiles');
      setData(d);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load PF profiles'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft.employee_id.trim()) { toast.error('An employee id is needed'); return; }
    setSaving(true);
    try {
      await api.put(`/api/pf/profiles/${draft.employee_id.trim()}`, { uan: draft.uan.trim() });
      toast.success('UAN saved');
      setDraft({ employee_id: '', uan: '' });
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save that UAN'));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <CardSkeleton rows={6} />;

  // An exempt member is not in the ECR at all, so a missing UAN on one does not
  // block the file. Counting them would send HR chasing a number nobody needs.
  const exemptIds = new Set(data.profiles.filter((p) => p.is_exempt).map((p) => p.employee_id));
  const blockingEcr = data.without_uan.filter((id) => !exemptIds.has(id));

  return (
    <div className="space-y-4">
      {blockingEcr.length > 0 && (
        <div className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[13px] text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>{blockingEcr.length} covered member(s) have no UAN.</b> The ECR file
            cannot be generated until every one of them has one — the EPFO rejects
            a file with a missing UAN, and a partial upload looks like it worked.
          </div>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Add or update a UAN</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Employee ID</Label>
            <Input className="mt-1 w-40" value={draft.employee_id}
              onChange={(e) => setDraft({ ...draft, employee_id: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">UAN (12 digits)</Label>
            <Input className="mt-1 w-48" value={draft.uan}
              onChange={(e) => setDraft({ ...draft, uan: e.target.value })} />
          </div>
          <Button onClick={save} disabled={saving}>Save</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Profiles</CardTitle></CardHeader>
        <CardContent className="p-0">
          {data.profiles.length === 0 ? (
            <EmptyState icon={Landmark} title="Nobody to cover yet"
              description="Every active employee appears here, whether or not a PF profile has been created for them." />
          ) : (
            <Table className="text-hr-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>UAN</TableHead>
                  <TableHead>PF account</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.profiles.map((p) => (
                  <TableRow key={p.employee_id} className={p.is_exempt ? 'opacity-70' : ''}>
                    <TableCell className="font-medium">
                      {p.employee_name || p.employee_id}
                      {p.employee_name && (
                        <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                          {p.employee_id}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {p.uan
                        ? p.uan
                        : p.is_exempt
                          ? <span className="text-muted-foreground">not required</span>
                          : <Badge className="bg-amber-500/15 text-amber-700">missing</Badge>}
                    </TableCell>
                    <TableCell>{p.pf_account_number || '—'}</TableCell>
                    <TableCell>
                      {p.is_exempt ? (
                        <div>
                          <Badge variant="outline">
                            <ShieldOff className="mr-1 inline h-3 w-3" />exempt
                          </Badge>
                          {p.exemption_reason && (
                            <div className="mt-1 max-w-xs text-[11px] text-muted-foreground">
                              {p.exemption_reason}
                            </div>
                          )}
                        </div>
                      ) : <Badge variant="soft">covered</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setExemptionTarget(p)}>
                        {p.is_exempt ? 'Restore to PF' : 'Exempt'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {data.profiles.some((p) => p.is_exempt) && (
            <p className="border-t p-3 text-xs text-muted-foreground">
              Exempt members are left out of contributions and the ECR from the next
              generation onward. Months already generated keep the figures they were
              generated with.
            </p>
          )}
        </CardContent>
      </Card>

      <ExemptionDialog
        profile={exemptionTarget}
        onClose={() => setExemptionTarget(null)}
        onSaved={load}
      />
    </div>
  );
};

/* ──────────────────────── contributions and the ECR ──────────────────────── */

const PfContributions = () => {
  const [period, setPeriod] = useState(previousMonth());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  // Published descriptions of ECR 2.0 disagree on this, and the portal asks
  // which one the file uses. Cheaper to offer both than to be wrong once.
  const [delimiter, setDelimiter] = useState('#~#');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/pf/contributions?month=${period.month}&year=${period.year}`);
      setRows(data.contributions || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [period.month, period.year]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy('generate');
    try {
      const { data } = await api.post('/api/pf/contributions/generate', period);
      toast.success(`${data.created} created, ${data.updated} updated`, {
        description: data.missing_uan.length
          ? `${data.missing_uan.length} without a UAN — the ECR needs one for each`
          : undefined,
      });
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not generate contributions'));
    } finally {
      setBusy('');
    }
  };

  const downloadEcr = async () => {
    setBusy('ecr');
    try {
      const { data } = await api.get('/api/pf/ecr', {
        params: { month: period.month, year: period.year, delimiter },
        responseType: 'text',
      });
      const blob = new Blob([data], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ECR_${period.year}_${String(period.month).padStart(2, '0')}.txt`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getErrorMessage(e, 'The ECR could not be generated'));
    } finally {
      setBusy('');
    }
  };

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    employee: acc.employee + Number(r.employee_contribution || 0),
    employer: acc.employer + Number(r.employer_pf_contribution || 0),
    pension: acc.pension + Number(r.employer_pension_contribution || 0),
  }), { employee: 0, employer: 0, pension: 0 }), [rows]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly contributions</CardTitle>
          <p className="text-[13px] text-muted-foreground">
            Derived from the month&apos;s payslips, so a return always matches what
            was actually paid. Process payroll first.
          </p>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-2">
          <PeriodPicker period={period} onChange={setPeriod} />
          <Button onClick={generate} disabled={busy === 'generate'}>
            <RefreshCw className={`mr-2 h-4 w-4 ${busy === 'generate' ? 'animate-spin' : ''}`} />
            Generate
          </Button>
          <div>
            <Label className="text-xs text-muted-foreground">ECR delimiter</Label>
            <select
              value={delimiter}
              onChange={(e) => setDelimiter(e.target.value)}
              className="mt-1 h-10 rounded-md border border-input bg-background px-3 font-mono text-sm"
            >
              <option value="#~#">#~#</option>
              <option value="||">||</option>
            </select>
          </div>
          <Button variant="outline" onClick={downloadEcr} disabled={busy === 'ecr' || rows.length === 0}>
            <Download className="mr-2 h-4 w-4" />Download ECR
          </Button>
          <p className="basis-full text-[11px] text-muted-foreground">
            Eleven fields per line (ECR 2.0). Published sources disagree on the
            delimiter — use whichever the portal asks for, and check the column
            layout against the current EPFO specification before uploading.
          </p>
        </CardContent>
      </Card>

      {loading ? <CardSkeleton rows={6} /> : rows.length === 0 ? (
        <EmptyState icon={FileText} title="Nothing for this month"
          description="Generate contributions once payroll for the month has been processed." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table className="text-hr-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>UAN</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">EPF wages</TableHead>
                  <TableHead className="text-right">Employee</TableHead>
                  <TableHead className="text-right">Employer PF</TableHead>
                  <TableHead className="text-right">Pension</TableHead>
                  <TableHead className="text-right">NCP days</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.employee_id}>
                    <TableCell className="font-medium">{r.employee_id}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.uan || <Badge className="bg-amber-500/15 text-amber-700">missing</Badge>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.gross_wages)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.epf_wages)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.employee_contribution)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.employer_pf_contribution)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.employer_pension_contribution)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.ncp_days}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/40 font-medium">
                  <TableCell colSpan={4}>{rows.length} employee(s)</TableCell>
                  <TableCell className="text-right tabular-nums">{money(totals.employee)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(totals.employer)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(totals.pension)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ────────────────────────────── challans ────────────────────────────── */

const PfChallans = () => {
  const [period, setPeriod] = useState(previousMonth());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/pf/challans');
      setRows(data.challans || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load challans'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy('generate');
    try {
      const { data } = await api.post('/api/pf/challans/generate', period);
      toast.success(`Challan ready — ${money(data.grand_total)} for ${data.employee_count} employee(s)`);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not generate the challan'));
    } finally {
      setBusy('');
    }
  };

  const markPaid = async (row) => {
    const reference = window.prompt('Payment reference for this challan:');
    if (reference === null) return;
    try {
      await api.post(`/api/pf/challans/${row.id}/mark-paid`, { payment_reference: reference });
      toast.success('Challan marked paid');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not mark it paid'));
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Generate a challan</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-2">
          <PeriodPicker period={period} onChange={setPeriod} />
          <Button onClick={generate} disabled={busy === 'generate'}>Generate</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Challans</CardTitle></CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <EmptyState icon={Wallet} title="No challans yet"
              description="Generate one once the month's contributions exist." />
          ) : (
            <Table className="text-hr-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Employee</TableHead>
                  <TableHead className="text-right">Employer PF</TableHead>
                  <TableHead className="text-right">Pension</TableHead>
                  <TableHead className="text-right">Admin + EDLI</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{MONTHS[r.month - 1]} {r.year}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.employee_total)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.employer_pf_total)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.pension_total)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(Number(r.admin_charges) + Number(r.edli_charges))}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{money(r.grand_total)}</TableCell>
                    <TableCell>
                      {r.status === 'paid'
                        ? <Badge className="bg-emerald-500/15 text-emerald-700">
                            <CheckCircle2 className="mr-1 inline h-3 w-3" />paid
                          </Badge>
                        : <Badge variant="soft">{r.status}</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.status !== 'paid' && (
                        <Button size="sm" variant="outline" onClick={() => markPaid(r)}>Mark paid</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const PfModule = ({ section = 'policy' }) => {
  if (section === 'employees') return <PfProfiles />;
  if (section === 'contributions') return <PfContributions />;
  if (section === 'challans') return <PfChallans />;
  return <PfPolicy />;
};

export default PfModule;
